import { useId } from "react";
import { cn } from "@/lib/utils";

/**
 * One categorical slice. `color` is a full CSS color value (typically a design
 * token, e.g. `"var(--chart-1)"` or `"var(--success)"`) so the chart stays
 * theme-aware — the browser resolves the variable per light/dark automatically.
 */
export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  slices: DonutSlice[];
  /** Accessible name for the figure (announced to screen readers). */
  title: string;
  /** Small caption under the center total (e.g. "holders"). */
  centerLabel?: string;
  /** Outer diameter in px. */
  size?: number;
  /** Ring thickness in px. */
  thickness?: number;
  /** Formats slice + total values for display. Defaults to a plain integer. */
  formatValue?: (n: number) => string;
  className?: string;
  /**
   * When provided, arcs + legend rows become interactive (click a segment to
   * select it). Receives the index into `slices`. Absent = a static chart.
   */
  onSelect?: (index: number) => void;
  /** Index of the currently-selected slice — highlighted; others dim. */
  selectedIndex?: number | null;
}

const GAP_PX = 2; // surface-color gap between touching segments (marks spec)

/**
 * A hand-built categorical donut: colored ring + centered total + a text
 * legend. Generic — data in, SVG out; no charting dependency.
 *
 * Accessibility: the SVG carries `role="img"` + `<title>`/`<desc>`, and the
 * legend below restates every slice as label · value · percent — so it doubles
 * as the data-table fallback and identity never rests on color alone (the
 * brand `--chart-*` ramp is an analogous blue→purple gradient whose adjacent
 * CVD separation sits in the floor band, so the text legend + segment gaps are
 * the mandated secondary encoding). Handles the empty case (total 0) by drawing
 * a neutral track ring.
 */
export function DonutChart({
  slices,
  title,
  centerLabel,
  size = 168,
  thickness = 22,
  formatValue = (n) => n.toLocaleString(),
  className,
  onSelect,
  selectedIndex,
}: DonutChartProps) {
  const titleId = useId();
  const descId = useId();

  const total = slices.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Only non-empty slices get a drawn arc; each is trimmed by the 2px gap so
  // neighbours read as separated by surface, not a stroke. `index` is the
  // slice's position in the original array (what `onSelect` reports).
  const arcs: { slice: DonutSlice; index: number; dash: number; gap: number; offset: number }[] =
    [];
  if (total > 0) {
    let cumulative = 0;
    slices.forEach((slice, index) => {
      if (slice.value <= 0) return;
      const fraction = slice.value / total;
      const dash = Math.max(fraction * circumference - GAP_PX, 0.5);
      arcs.push({
        slice,
        index,
        dash,
        gap: circumference - dash,
        offset: -(cumulative * circumference),
      });
      cumulative += fraction;
    });
  }
  const dimUnselected = (index: number) =>
    selectedIndex != null && selectedIndex !== index ? 0.3 : 1;

  const desc =
    total > 0
      ? slices
          .map(
            (s) => `${s.label}: ${formatValue(s.value)} (${Math.round((s.value / total) * 100)}%)`,
          )
          .join("; ")
      : "No data.";

  return (
    <div className={cn("flex flex-col items-center gap-4 sm:flex-row sm:items-center", className)}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-labelledby={`${titleId} ${descId}`}
        >
          <title id={titleId}>{title}</title>
          <desc id={descId}>{desc}</desc>
          {/* Track — also the empty state when total is 0. */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--border)"
            strokeWidth={thickness}
          />
          <g transform={`rotate(-90 ${center} ${center})`}>
            {arcs.map(({ slice, index, dash, gap, offset }) => (
              <circle
                key={slice.label}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={slice.color}
                strokeWidth={thickness}
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={offset}
                onClick={onSelect ? () => onSelect(index) : undefined}
                style={{
                  opacity: dimUnselected(index),
                  cursor: onSelect ? "pointer" : undefined,
                  transition: "opacity 150ms",
                }}
              />
            ))}
          </g>
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-foreground">{formatValue(total)}</span>
          {centerLabel && <span className="text-xs text-muted-foreground">{centerLabel}</span>}
        </div>
      </div>

      <ul className="min-w-0 flex-1 space-y-1.5">
        {slices.map((slice, index) => {
          const percent = total > 0 ? Math.round((slice.value / total) * 100) : 0;
          const row = (
            <>
              <span
                aria-hidden="true"
                className="size-2.5 shrink-0 rounded-[3px]"
                style={{ backgroundColor: slice.color }}
              />
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{slice.label}</span>
              <span className="shrink-0 font-medium tabular-nums text-foreground">
                {formatValue(slice.value)}
              </span>
              <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {percent}%
              </span>
            </>
          );
          return (
            <li key={slice.label} style={{ opacity: dimUnselected(index) }}>
              {onSelect ? (
                <button
                  type="button"
                  aria-pressed={selectedIndex === index}
                  onClick={() => onSelect(index)}
                  className={cn(
                    "-mx-1.5 flex w-[calc(100%+0.75rem)] items-center gap-2 rounded px-1.5 py-1 text-left text-sm transition-colors hover:bg-accent",
                    selectedIndex === index && "bg-accent",
                  )}
                >
                  {row}
                </button>
              ) : (
                <div className="flex items-center gap-2 text-sm">{row}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
